/**
 * Ernaehrung (F-35): Datenmodell. Ole plant und erfasst selbst - die App
 * rechnet und wertet aus, gibt aber keine Diaet-Vorgaben.
 */
import type { ID, ISOInstant, LocalDate } from '@/domain/types';
import type { Kriterium } from '@/domain/ernaehrung/analyse';
import type { ReferenzGruppe } from '@/domain/ernaehrung/referenz';

/** Mikronaehrstoffe, die die App kennt - welche angezeigt werden, ist einstellbar. */
export type Mikro = 'eisen' | 'calcium' | 'magnesium' | 'vitaminC' | 'vitaminD' | 'b12';

export const MIKROS: Record<Mikro, { label: string; einheit: 'mg' | 'µg' }> = {
  eisen: { label: 'Eisen', einheit: 'mg' },
  calcium: { label: 'Calcium', einheit: 'mg' },
  magnesium: { label: 'Magnesium', einheit: 'mg' },
  vitaminC: { label: 'Vitamin C', einheit: 'mg' },
  vitaminD: { label: 'Vitamin D', einheit: 'µg' },
  b12: { label: 'Vitamin B12', einheit: 'µg' },
};

/**
 * Naehrwerte - pro 100 g bzw. 100 ml (Lebensmittel) oder absolut (Summe).
 * Fehlt ein Feld, ist der Wert unbekannt (nicht 0) - wichtig fuer
 * „unvollstaendig“ statt „Mangel“.
 */
export interface Naehrwerte {
  kcal?: number;
  fett?: number;
  gesFett?: number;
  kh?: number;
  zucker?: number;
  ballast?: number;
  eiweiss?: number;
  salz?: number;
  mikro?: Partial<Record<Mikro, number>>;
}

export type MakroKey = Exclude<keyof Naehrwerte, 'mikro'>;
export const MAKROS: Record<MakroKey, { label: string; einheit: 'kcal' | 'g' }> = {
  kcal: { label: 'Energie', einheit: 'kcal' },
  eiweiss: { label: 'Eiweiß', einheit: 'g' },
  kh: { label: 'Kohlenhydrate', einheit: 'g' },
  zucker: { label: 'davon Zucker', einheit: 'g' },
  fett: { label: 'Fett', einheit: 'g' },
  gesFett: { label: 'davon gesättigte Fettsäuren', einheit: 'g' },
  ballast: { label: 'Ballaststoffe', einheit: 'g' },
  salz: { label: 'Salz', einheit: 'g' },
};

/** Lebensmittelgruppen fuer Portionen und die Einkaufsliste. */
export type LebensmittelGruppe = 'gemuese' | 'obst' | 'vollkorn' | 'eiweiss' | 'milch' | 'suess' | 'fastfood' | 'getraenk' | 'sonstiges';

export const GRUPPEN: Record<LebensmittelGruppe, { label: string; abteilung: string }> = {
  gemuese: { label: 'Gemüse', abteilung: 'Obst & Gemüse' },
  obst: { label: 'Obst', abteilung: 'Obst & Gemüse' },
  vollkorn: { label: 'Vollkorn', abteilung: 'Brot & Backwaren' },
  eiweiss: { label: 'Eiweißquellen', abteilung: 'Fleisch & Fisch' },
  milch: { label: 'Milchprodukte', abteilung: 'Kühlregal' },
  suess: { label: 'Süßes/Snacks', abteilung: 'Süßes & Snacks' },
  fastfood: { label: 'Fast Food', abteilung: 'Tiefkühl' },
  getraenk: { label: 'Getränke', abteilung: 'Getränke' },
  sonstiges: { label: 'Sonstiges', abteilung: 'Vorrat' },
};

/** Standardportion ohne Waage, z. B. „1 Banane“ = 120 g. */
export interface Portion { name: string; menge: number }

export interface Lebensmittel {
  id: ID;
  name: string;
  marke?: string;
  barcode?: string;
  /** Bezugsgroesse der Naehrwerte: pro 100 g oder 100 ml. */
  basis: 'g' | 'ml';
  je100: Naehrwerte;
  gruppe?: LebensmittelGruppe;
  portionen?: Portion[];
  /** Woher die Werte kommen. */
  quelle: 'eigen' | 'openfoodfacts' | 'foto';
  /** Zuletzt benutzt - fuer „zuletzt und haeufig“ oben. */
  zuletzt?: ISOInstant;
  anzahl?: number;
  archiviert?: boolean;
  createdAt: ISOInstant;
  updatedAt: ISOInstant;
}

export interface Zutat { id: ID; lebensmittelId: ID; menge: number }

export interface Rezept {
  id: ID;
  name: string;
  /** Wie viele Portionen das Rezept ergibt. */
  portionen: number;
  zutaten: Zutat[];
  zubereitung?: string;
  gruppe?: LebensmittelGruppe;
  zuletzt?: ISOInstant;
  anzahl?: number;
  createdAt: ISOInstant;
  updatedAt: ISOInstant;
}

/**
 * Ein Posten in Plan oder Tagebuch: Lebensmittel (Menge in g/ml), Rezept
 * (Menge in Portionen) oder Schnelleintrag (nur Werte).
 */
export type Posten = (
  | { id: ID; art: 'lebensmittel'; refId: ID; menge: number; anzeige?: string }
  | { id: ID; art: 'rezept'; refId: ID; menge: number }
  | { id: ID; art: 'schnell'; name: string; werte: Naehrwerte }
) & {
  /** Im Tagebuch: aus diesem Plan-Posten uebernommen (B). */
  vonPlan?: ID;
};

export interface Mahlzeit { id: ID; name: string; posten: Posten[] }

export const STANDARD_MAHLZEITEN = ['Frühstück', 'Mittag', 'Abend', 'Snacks'];

/** Speiseplan eines Tages (`id` = Datum). */
export interface PlanTag {
  id: LocalDate;
  datum: LocalDate;
  mahlzeiten: Mahlzeit[];
  updatedAt: ISOInstant;
}

/** Gespeicherter Tagesplan, z. B. „Trainingstag“. */
export interface PlanVorlage {
  id: ID;
  name: string;
  mahlzeiten: Mahlzeit[];
  createdAt: ISOInstant;
  updatedAt: ISOInstant;
}

// --- Erfassen und Ziele (Stufen B-D) ------------------------------------------

/** Was an einem Tag gegessen wurde (`id` = Datum) - gleich aufgebaut wie der Plan. */
export interface GegessenTag {
  id: LocalDate;
  datum: LocalDate;
  mahlzeiten: Mahlzeit[];
  /** Von Hand: Tag ist vollstaendig erfasst (auch mit weniger Mahlzeiten, z. B. Fasten). */
  vollstaendig?: boolean;
  updatedAt: ISOInstant;
}

export type GetraenkArt = 'wasser' | 'tee' | 'kaffee' | 'saft' | 'softdrink' | 'milch' | 'sonstiges';

export const GETRAENKE: Record<GetraenkArt, string> = {
  wasser: 'Wasser', tee: 'Tee', kaffee: 'Kaffee', saft: 'Saft', softdrink: 'Softdrink', milch: 'Milch', sonstiges: 'Sonstiges',
};

export interface Getraenk {
  id: ID;
  datum: LocalDate;
  /** `HH:mm` - fuer den Verlauf ueber den Tag. */
  zeit: string;
  art: GetraenkArt;
  ml: number;
  /** Zuckerhaltige Getraenke: Naehrwerte aus diesem Lebensmittel (pro 100 ml). */
  lebensmittelId?: ID;
  createdAt: ISOInstant;
}

/** Tagesziele - alle optional, Ole legt sie selbst fest. */
export interface Tagesziele {
  kcal?: number;
  eiweiss?: number;
  kh?: number;
  fett?: number;
  ballast?: number;
  zucker?: number;
  salz?: number;
  wasserMl?: number;
}

export interface ErnaehrungsEinstellungen {
  id: 'standard';
  /** Ziele fuer Ruhetage bzw. alle Tage. */
  ziele: Tagesziele;
  /** Abweichende Ziele an Trainingstagen (F-34) - fehlt = wie Ruhetag. */
  zieleTraining?: Tagesziele;
  /** Namen der Mahlzeiten in dieser Reihenfolge. */
  mahlzeiten?: string[];
  /** Angezeigte Mikronaehrstoffe. */
  mikros?: Mikro[];
  /** Offizielle Referenzwerte einblenden (D). */
  referenz?: boolean;
  /** Fuer welche Gruppe die Referenzwerte gelten (D) - Standard Maenner. */
  referenzGruppe?: ReferenzGruppe;
  /** Kriterien des Ernaehrungs-Scores (D) - fehlt = Ausgangsauswahl. */
  kriterien?: Kriterium[];
  /** Foto der Naehrwerttabelle nach dem Speichern behalten (B). */
  fotoBehalten?: boolean;
  /** Erlaubte Abweichung bei Spannen-Zielen (kcal, KH, Fett) in Prozent - Standard 10. */
  spanne?: number;
  /** Ab so vielen erfassten Mahlzeiten gilt ein Tag als vollstaendig - Standard 3. */
  mindestMahlzeiten?: number;
  /** Bereich fuer Ziele und Board (E). */
  bereichId?: ID;
  updatedAt: ISOInstant;
}
